import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFolder } from "src/core/contexts/FolderContext";
import { useFolderGraphSync } from "src/core/contexts/FolderGraphSyncContext";
import {
  RenvoiCompletionFormValues,
  renvoiCompletionSchema,
} from "src/core/forms/folder/renvoiCompletion.schema";
import { useExecuteWorkflowActionMutation } from "src/core/gql/graphql";
import { useToast } from "src/core/hooks/use-toast";
import { buildFolderFilesCountRefetch } from "src/core/utils/refetchers";

export const useRenvoiCompletion = (setShowConfirmDialog: (show: boolean) => void) => {
  const { t } = useTranslation();
  const { folderFile, refetch } = useFolder();
  const [isLoading, setIsLoading] = useState(false);
  const [executeWorkflowAction] = useExecuteWorkflowActionMutation();
  const { toast } = useToast();
  const form = useForm<RenvoiCompletionFormValues>({
    resolver: zodResolver(renvoiCompletionSchema(t)),
    defaultValues: {
      motifRenvoi: "",
    },
  });

  const { setSelectedTab } = useFolderGraphSync();

  const onSubmit = useCallback(
    async (data: RenvoiCompletionFormValues) => {
      const folderFileId = folderFile.folderFile?.id;

      if (!folderFileId) {
        toast({
          description: t("global.form.fail"),
          variant: "destructive",
        });
        return;
      }

      try {
        setIsLoading(true);
        await executeWorkflowAction({
          variables: {
            actionId: "EDIT",
            folderFileId: folderFileId,
            comment: data.motifRenvoi.trim(),
          },
          awaitRefetchQueries: true,
          refetchQueries: buildFolderFilesCountRefetch(),
        });
        await refetch();
        toast({
          description: t("global.form.success"),
        });
        setShowConfirmDialog(false);
        form.reset();

        setSelectedTab("entreprise");
      } catch (error) {
        toast({
          description: t("global.form.fail"),
          variant: "destructive",
        });
        console.error("Erreur lors renvoi en completion:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      folderFile,
      form,
      refetch,
      setShowConfirmDialog,
      setSelectedTab,
      toast,
      executeWorkflowAction,
      t,
    ]
  );

  const handleCancel = () => {
    setShowConfirmDialog(false);
    form.reset();
  };

  return {
    isLoading,
    form,
    onSubmit,
    handleCancel,
  };
};
